/**
 * OfficialsPay V3 Data Model Types
 *
 * Canonical type definitions for the v3db Firestore database.
 * All timestamps are Firestore Timestamp (no string unions).
 *
 * @see design/v3/DATA-MODEL.md
 */
import { Timestamp } from "firebase/firestore";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type V3GameStatus =
  | "upcoming"
  | "completed"
  | "paid"
  | "cancelled"
  | "missed";

export type V3PayStatus = "unpaid" | "partial" | "paid";

export type V3ImportSource =
  | "manual"
  | "arbiter"
  | "horizon"
  | "gamechanger"
  | "assignr"
  | "ai_voice"
  | "ai_import"
  | "demo"
  | "spreadsheet";

export type V3PersonRoleType =
  | "official"
  | "assignor"
  | "administrator"
  | "coach"
  | "scorekeeper"
  | "contact"
  | "other";

export type V3ExpenseCategory =
  | "equipment_supplies"
  | "travel_meals"
  | "uniforms_gear"
  | "training_certification"
  | "dues_memberships"
  | "communication"
  | "office_supplies"
  | "insurance"
  | "other";

export type V3ExpenseSource = "manual" | "receipt_scan" | "imported" | "voice";

export type V3ConfidenceLevel = "high" | "medium" | "low";

export type V3MileageStatus = "auto" | "confirmed" | "edited" | "no_travel";

export type V3VoiceIntent =
  | "add_game"
  | "debrief"
  | "log_expense"
  | "general_note"
  | "update_game"
  | "question";

export type V3VoiceNoteStatus =
  | "recording"
  | "transcribing"
  | "processing"
  | "pending_review"
  | "processed"
  | "dismissed";

export type V3ImportJobStatus =
  | "uploading"
  | "parsing"
  | "preview"
  | "confirmed"
  | "importing"
  | "completed"
  | "failed";

export type V3UserCohort = "rookie" | "veteran" | "crew_chief";

export type V3MileageCalculationMethod =
  | "google_maps"
  | "straight_line"
  | "game_record"
  | "manual";

export type V3FilingStatus =
  | "single"
  | "married_joint"
  | "married_separate"
  | "head_of_household";

// ---------------------------------------------------------------------------
// Embedded / Sub-document Types
// ---------------------------------------------------------------------------

export interface V3Coordinates {
  lat: number;
  lng: number;
}

export interface V3OnboardingState {
  welcomed: boolean;
  homeAddressSet: boolean;
  firstGameAdded: boolean;
  firstImportDone: boolean;
  voiceIntroSeen: boolean;
}

export interface V3CrewMember {
  personId: string;
  name: string;
  position: string;
  isUser: boolean;
  phone?: string;
}

export interface V3GameContact {
  personId?: string;
  name: string;
  role: string;
  phone?: string;
  email?: string;
  organizationName?: string;
  teamName?: string;
}

export interface V3PayInfo {
  promisedAmount: number;
  paidAmount?: number;
  status: V3PayStatus;
  paidDate?: Timestamp;
  estimatedPaymentDate?: Timestamp;
  method?: string;
  notes?: string;
}

export interface V3MileageInfo {
  startAddress: string;
  startCoordinates?: V3Coordinates;
  gameAddress: string;
  gameCoordinates?: V3Coordinates;
  endAddress?: string;
  endCoordinates?: V3Coordinates;
  milesToGame: number;
  milesFromGame: number;
  totalDeductible: number;
  isManual: boolean;
  calculationMethod: V3MileageCalculationMethod;
  noTravel: boolean;
  tripConfirmedAt?: Timestamp;
  mileageReconciledAt?: Timestamp;
}

export interface V3DebriefData {
  transcript: string;
  summary?: string;
  keyMoments?: string[];
  partnerFeedback?: string;
  selfAssessment?: string;
  createdAt: Timestamp;
}

export interface V3WeatherData {
  temperature?: number;
  condition?: string;
  humidity?: number;
  windSpeed?: number;
  icon?: string;
  fetchedAt: Timestamp;
}

export interface V3PersonRole {
  type: V3PersonRoleType;
  title?: string;
  organizationId?: string;
  teamId?: string;
  sport?: string;
  positions?: string[];
}

export interface V3Affiliation {
  organizationId: string;
  organizationName: string;
  role: V3PersonRoleType;
  title?: string;
  teamId?: string;
  teamName?: string;
  startDate?: Timestamp;
  endDate?: Timestamp;
}

export interface V3PersonStats {
  totalGames: number;
  gamesThisYear: number;
  lastGameDate?: Timestamp;
  gamesAsCrew: number;
  gamesAsContact: number;
  computedAt: Timestamp;
}

export interface V3OrgLevel {
  name: string;
  defaultPay: number;
}

export interface V3ReceiptItem {
  description: string;
  amount: number;
  category?: V3ExpenseCategory;
  isDeductible: boolean;
  confidence?: V3ConfidenceLevel;
}

export interface V3ExpenseMatch {
  gameId: string;
  gameDateISO: string;
  gameLocation: string;
  matchType: "date_and_location" | "date_only" | "day_before" | "manual";
  confidence: V3ConfidenceLevel;
  distanceMiles?: number;
  confirmedByUser: boolean;
  confirmedAt?: Timestamp;
}

export interface V3ReadinessItem {
  key: string;
  label: string;
  completed: boolean;
  completedAt?: Timestamp;
  weight: number;
}

export interface V3Received1099 {
  organizationId: string;
  organizationName: string;
  amount: number;
  receivedDate?: Timestamp;
}

export interface V3AgentAction {
  type: string;
  entityType: string;
  entityId?: string;
  description: string;
  timestamp: Timestamp;
  confidence: V3ConfidenceLevel;
  confirmedByUser?: boolean;
}

export interface V3ImportError {
  row?: number;
  field?: string;
  message: string;
  severity: "warning" | "error";
}

// ---------------------------------------------------------------------------
// Top-level Entity Types
// ---------------------------------------------------------------------------

export interface V3UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  cohort: V3UserCohort;
  sport?: string;
  sports?: string[];
  timezone?: string;
  homeAddress?: string;
  homeCoordinates?: V3Coordinates;
  selfPersonId?: string;
  onboarding: V3OnboardingState;
  createdAt: Timestamp;
  lastLoginAt?: Timestamp;
  loginCount: number;
  stripeCustomerId?: string;
}

export interface V3Game {
  id: string;
  dateTime: Timestamp;
  endTime?: Timestamp;
  sport: string;
  level?: string;
  organizationId: string;
  locationId?: string;
  locationName: string;
  homeTeamId?: string;
  awayTeamId?: string;
  homeTeamName?: string;
  awayTeamName?: string;
  crew: V3CrewMember[];
  contacts?: V3GameContact[];
  pay: V3PayInfo;
  mileage: V3MileageInfo;
  status: V3GameStatus;
  cancellationReason?: string;
  notes?: string;
  voiceNoteIds?: string[];
  debrief?: V3DebriefData;
  tags?: string[];
  gcalEventId?: string;
  weather?: V3WeatherData;
  importSource?: V3ImportSource;
  importSourceFile?: string;
  importedAt?: Timestamp;
  importJobId?: string;
  /** Horizon WebRef "Game ID#" — stable key for schedule reconciliation */
  horizonGameId?: string;
  isDemoData?: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3Person {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
  roles: V3PersonRole[];
  primaryRole: V3PersonRoleType;
  affiliations?: V3Affiliation[];
  isUser: boolean;
  isPartner: boolean;
  preferredPosition?: string;
  stats?: V3PersonStats;
  importSources?: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastSeenAt?: Timestamp;
}

export interface V3Organization {
  id: string;
  name: string;
  sport: string;
  alternateNames?: string[];
  orgProfileName?: string;
  levels?: V3OrgLevel[];
  contactPersonIds?: string[];
  defaultPayMethod?: string;
  paymentAccountInfo?: string;
  paymentNotes?: string;
  ruleUrl?: string;
  ruleset?: string;
  calendarSearchTerm?: string;
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3Team {
  id: string;
  name: string;
  organizationId?: string;
  coachPersonId?: string;
  level?: string;
  sport?: string;
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3Location {
  id: string;
  name: string;
  /** Other labels for the same place (e.g. geocoded address vs venue name) */
  alternateNames?: string[];
  address?: string;
  coordinates?: V3Coordinates;
  distanceFromHome?: number;
  travelTimeFromHome?: number;
  organizationIds?: string[];
  parkingNotes?: string;
  fieldNotes?: string;
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3Expense {
  id: string;
  date: Timestamp;
  vendor: string;
  amount: number;
  description: string;
  notes?: string;
  category: V3ExpenseCategory;
  taxDeductible: boolean;
  deductiblePercentage: number;
  receiptUrl?: string;
  receiptItems?: V3ReceiptItem[];
  receiptParsedAt?: Timestamp;
  receiptParseConfidence?: V3ConfidenceLevel;
  receiptLocation?: string;
  gameIds?: string[];
  gameMatch?: V3ExpenseMatch;
  autoLinked: boolean;
  taxYear: string;
  source: V3ExpenseSource;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * @deprecated Not the canonical mileage store. The live UI (`/finances/mileage`,
 * via `src/app/finances/mileage/page.tsx`) treats per-game `game.miles` +
 * `game.mileageReconciledAt` as the source of truth. `V3MileageTrip` and the
 * `mileageTrips` subcollection are a parallel model that is not written from
 * any UI path. Do not write new code against this; see
 * `design/finance-lifecycle-review.md` §5 Phase 1c.
 */
export interface V3MileageTrip {
  id: string;
  gameId?: string;
  gameDateISO: string;
  tripDate: Timestamp;
  description?: string;
  fromAddress: string;
  fromCoordinates?: V3Coordinates;
  toAddress: string;
  toCoordinates?: V3Coordinates;
  endAddress?: string;
  milesOneWay: number;
  milesRoundTrip: number;
  milesDeductible: number;
  isManual: boolean;
  calculationMethod: V3MileageCalculationMethod;
  calculatedAt?: Timestamp;
  status: V3MileageStatus;
  confirmedAt?: Timestamp;
  confirmedBy?: string;
  taxYear: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3TaxYearConfig {
  year: string;
  mileageRate: number;
  mileageRateSource: "irs_default" | "user_override";
  irsOfficialRate: number;
  federalTaxRate: number;
  stateTaxRate?: number;
  combinedTaxRate: number;
  filingStatus: V3FilingStatus;
  homeAddress: string;
  homeCoordinates?: V3Coordinates;
  readinessItems: V3ReadinessItem[];
  readinessPercentage: number;
  received1099s?: V3Received1099[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3VoiceNote {
  id: string;
  transcript: string;
  audioUrl?: string;
  duration?: number;
  intent?: V3VoiceIntent;
  linkedGameIds?: string[];
  linkedExpenseIds?: string[];
  agentActions?: V3AgentAction[];
  status: V3VoiceNoteStatus;
  context?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3ImportJob {
  id: string;
  fileName: string;
  fileUrl?: string;
  fileType: string;
  detectedFormat?: string;
  status: V3ImportJobStatus;
  gamesFound?: number;
  gamesImported?: number;
  gamesSkipped?: number;
  errors?: V3ImportError[];
  preview?: unknown;
  confirmedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface V3ExportRecord {
  id: string;
  taxYear?: string;
  exportDate: Timestamp;
  format: "csv" | "xlsx" | "pdf" | "json";
  dataSet: string;
  fileName: string;
  downloadUrl?: string;
  totalIncome?: number;
  totalExpenses?: number;
  totalMileage?: number;
  mileageDeduction?: number;
  gameCount?: number;
  createdAt: Timestamp;
}

// ---------------------------------------------------------------------------
// V3 Collection Names (for type-safe path building)
// ---------------------------------------------------------------------------

export const V3_COLLECTIONS = {
  games: "games",
  people: "people",
  organizations: "organizations",
  teams: "teams",
  locations: "locations",
  expenses: "expenses",
  /**
   * @deprecated Parallel mileage model not written from any UI path. Canonical
   * mileage lives on `games/{id}.miles` + `games/{id}.mileageReconciledAt`.
   * See `design/finance-lifecycle-review.md` §5 Phase 1c.
   */
  mileageTrips: "mileageTrips",
  taxConfig: "taxConfig",
  voiceNotes: "voiceNotes",
  exports: "exports",
  settings: "settings",
  invoices: "invoices",
  addresses: "addresses",
  importJobs: "importJobs",
  /** Server-side schedule import diagnostics (subcollection under `users/{uid}`). */
  parsingLogs: "parsingLogs",
} as const;

export type V3CollectionName =
  (typeof V3_COLLECTIONS)[keyof typeof V3_COLLECTIONS];

// ---------------------------------------------------------------------------
// Sport / Position Reference Data
// ---------------------------------------------------------------------------

export const V3_SPORT_POSITIONS: Record<string, string[]> = {
  baseball: ["Solo", "PU", "U1", "U2", "U3"],
  softball: ["Solo", "PU", "U1", "U2", "U3"],
  basketball: ["R", "U1", "U2"],
  football: ["R", "U", "HL", "LJ", "BJ", "FJ", "SJ"],
  soccer: ["CR", "AR1", "AR2"],
  hockey: ["R", "L1", "L2"],
  volleyball: ["R1", "R2", "Scorer"],
};

export const V3_SPORTS = Object.keys(V3_SPORT_POSITIONS);
